/**
 * Which page each permission opens — one table read by both the route guards
 * in routes.js (routeGuard) and the sidebar, so a link is offered exactly when
 * its page would open.
 *
 * Keys are the `path` strings exactly as written in routes.js ('' is the index
 * route). A rule of null opens the page to every signed-in member. Nested
 * child segments (INHERITED_PATHS) take their parent's guard. A path with no
 * rule is refused, and node-backend/tests/test-route-access.js fails when
 * routes.js gains a path this table does not know.
 *
 * CommonJS so the node-backend logic tests can require it.
 */

function rule(key, atLeast) {
    return { key: key, atLeast: atLeast || 'yes' };
}

var LIST = rule('properties.list');
var RECORDS = rule('properties.records', 'view');
var COMMISSION = rule('summit.commission', 'own');
var AGENT_PIPELINE = rule('summit.agent_pipeline', 'own');
var BUCKETS = rule('summit.buckets', 'view');
var PEAK_CLOSED = rule('peak.closed');
var PEAK_PIPELINE = rule('peak.pipeline');
var PEAK_TRACKING = rule('peak.tracking', 'view');
// Buyers, Sellers and Tenants are no longer sidebar pages (Investors replaced Buyers and Sellers); only their typed URLs remain.
var INVESTORS = rule('directory.investors');

var ROUTE_RULES = {
    '': rule('stats.dashboard'),
    'links': null,
    'training': null,
    'training/:slug': null,
    'training/:slug/:slug2': null,
    'print-booklet/:slug(/:chapterSlug)': null,
    'print-continuum/:slug': null,
    '*': null,
    'mail-merge': rule('mail_merge'),

    'map/parcels': LIST,
    'list': LIST,
    'property-groups': LIST,
    'map': LIST,
    'map/:id': LIST,
    'buyer-state-breakdown': LIST,
    'transaction-breakdown': LIST,
    'market-transaction-breakdown': LIST,
    'market-share': LIST,

    'property': rule('properties.create'),
    'property/:id': RECORDS,
    'property-statistics/:id': RECORDS,

    'agent-pipeline': AGENT_PIPELINE,
    'agent-pipeline/:id': AGENT_PIPELINE,
    'agent-pipeline/bucket/:bucket': rule('summit.agent_pipeline', 'all'),
    'inventory': rule('summit.inventory'),
    'escrow-tracking': rule('summit.escrow_tracking', 'view'),
    'buckets': BUCKETS,
    'buckets/:id': BUCKETS,
    'summit-fees': rule('summit.fees'),
    'agent-commission-breakdown': COMMISSION,
    'agent-commission-breakdown/paid-procured/:year/:id/procured': COMMISSION,
    'agent-commission-breakdown/paid-procured/:year/:id/paid': COMMISSION,
    'agent-commission-breakdown/:year/:id/procured': COMMISSION,
    'agent-commission-breakdown/:year/:id/paid': COMMISSION,

    'pipeline': PEAK_CLOSED,
    'pipeline/:status': PEAK_CLOSED,
    'tracking': PEAK_TRACKING,
    'hotlist': PEAK_PIPELINE,
    'hotlist/:id/:email': PEAK_PIPELINE,
    'target-list': PEAK_PIPELINE,
    'stats': rule('peak.stats'),

    'training-booklet': rule('training.booklet'),

    // Anyone with the team list; everyone for their own record (canOpenTeamMember).
    'teamMember/:id': rule('team.list'),
    'teamMember': rule('team.accounts', 'edit'),
    'teamMembers': rule('team.list'),
    'access-levels': rule('access.manage'),

    'proposals': rule('proposals', 'view'),
    'upload-file': rule('uploads'),
    'listing-sources': rule('listing_sources'),
    'true-owners': rule('true_owners'),
    'owner-research': rule('true_owners'),
    'geocoder': rule('geocoder'),
    'offices': rule('offices'),

    'firms': rule('directory.firms'),
    'firm': rule('directory.firms'),
    'investors': INVESTORS,
    'investor': INVESTORS,
    'buyer': INVESTORS,
    'agents': rule('directory.agents'),
    'agent': rule('directory.agents'),
    'sellers': INVESTORS,
    'seller': INVESTORS,
    'tenants': INVESTORS,
    'tenant': INVESTORS,
    'lenders': rule('directory.lenders'),
    'lender': rule('directory.lenders'),
    'vendors': rule('directory.vendors'),
    'vendor': rule('directory.vendors'),
};

/** Child segments nested under a guarded route; they take its guard. */
var INHERITED_PATHS = [':id', ':slug3', ':slug4', ':slug5', 'pipeline/:id'];

/** Sidebar Summit Pipeline link key → the route it opens. */
var SUMMIT_PIPELINE_LINK_ROUTES = {
    'agent-commission-breakdown': 'agent-commission-breakdown',
    'paid-procured': 'agent-commission-breakdown/paid-procured/:year/:id/paid',
    'agent-pipeline': 'agent-pipeline',
    'buckets': 'buckets',
    'inventory': 'inventory',
    'escrow-tracking': 'escrow-tracking',
    'summit-fees': 'summit-fees',
};

/**
 * Whether a page opens. `check(key, atLeast)` answers for the signed-in
 * member (access.js `can`). An unknown path does not open.
 */
function canOpenRoute(check, path) {
    if (!Object.prototype.hasOwnProperty.call(ROUTE_RULES, path)) return false;
    var r = ROUTE_RULES[path];
    return r === null || !!check(r.key, r.atLeast);
}

/**
 * Pages in the order the sidebar offers them, by route: where a member who
 * cannot open the home page is sent. Training is open to everyone, so the
 * last one always opens.
 */
var LANDING_ROUTES = [
    '', 'list', 'firms', 'investors', 'agents', 'vendors', 'offices',
    'agent-commission-breakdown', 'agent-pipeline', 'buckets', 'inventory', 'escrow-tracking', 'summit-fees',
    'proposals', 'target-list', 'tracking', 'pipeline', 'stats',
    'teamMembers', 'access-levels', 'training',
];

/**
 * The first page the sidebar offers that opens, or null when none does.
 * `check(key, atLeast)` answers for the signed-in member (access.js `can`).
 */
function firstOpenRoute(check) {
    for (var i = 0; i < LANDING_ROUTES.length; i++) {
        if (canOpenRoute(check, LANDING_ROUTES[i])) return LANDING_ROUTES[i];
    }
    return null;
}

/**
 * Where signing in lands. `landing` is the member's Landing page setting
 * (home.landing); it is a choice between two pages, not a permission: Peak
 * Stats when chosen and the member can open it, otherwise the Stats Dashboard,
 * otherwise the first page the sidebar offers. '' is the dashboard route.
 */
function landingRoute(check, landing) {
    if (landing === 'peak_stats' && canOpenRoute(check, 'stats')) return 'stats';
    if (canOpenRoute(check, '')) return '';
    return firstOpenRoute(check);
}

/** A team member's page: with the team list, or the member's own record. */
function canOpenTeamMember(check, memberId, ownId) {
    if (canOpenRoute(check, 'teamMember/:id')) return true;
    return ownId != null && ownId !== '' && memberId != null && memberId !== '' && String(memberId) === String(ownId);
}

module.exports = {
    ROUTE_RULES: ROUTE_RULES,
    INHERITED_PATHS: INHERITED_PATHS,
    SUMMIT_PIPELINE_LINK_ROUTES: SUMMIT_PIPELINE_LINK_ROUTES,
    LANDING_ROUTES: LANDING_ROUTES,
    canOpenRoute: canOpenRoute,
    firstOpenRoute: firstOpenRoute,
    landingRoute: landingRoute,
    canOpenTeamMember: canOpenTeamMember,
};
